import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/layout';
import { voyageService, carService, printService } from '../services';
import { Voyage, Car, Port, AdditionalFeeValue } from '../types';
import { SearchableSelect } from '../components';
import { extractMakeModel, sortRelatedCarsByType } from '../utils/carUtils';
import FreightManifest from './FreightManifest';

type ManifestCarItem = {
  blNo: string;
  consignee: string;
  chassisNo: string;
  packages: number;
  contents: string;
  weightKg: number;
  measurementM3: number;
  freight: string;
  isRelated?: boolean;
  relationshipType?: string;
};

type ShipperGroup = {
  shipperId: string;
  shipperName?: string;
  cars: ManifestCarItem[];
};

type ManifestType = 'freight' | 'cargo';

const FreightManifestPage: React.FC = () => {
  const [voyages, setVoyages] = useState<Voyage[]>([]);
  const [selectedVoyage, setSelectedVoyage] = useState<Voyage | null>(null);
  const [cars, setCars] = useState<Car[]>([]);
  const [selectedPortId, setSelectedPortId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [includeRelated, setIncludeRelated] = useState(true);
  const [manifestType, setManifestType] = useState<ManifestType>('freight');

  useEffect(() => {
    const loadVoyages = async () => {
      try {
        const data = await voyageService.getAll({ limit: 1000, is_archived: false });
        setVoyages(data as Voyage[]);
      } catch (error) {
        console.error('Error loading voyages:', error);
      }
    };
    loadVoyages();
  }, []);

  const sortedVoyages = useMemo(() => {
    return [...voyages].sort((a, b) => a.voyage_code.localeCompare(b.voyage_code));
  }, [voyages]);

  useEffect(() => {
    if (!selectedVoyage) {
      setCars([]);
      setSelectedPortId('');
      return;
    }

    const loadCars = async () => {
      setLoading(true);
      try {
        const response = await carService.getByVoyage(selectedVoyage.id, { limit: 10000 });
        setCars(response.data as Car[]);
      } catch (error) {
        console.error('Error loading cars:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCars();
  }, [selectedVoyage]);

  const availablePorts = useMemo<Port[]>(() => {
    const portMap = new Map<string, Port>();

    cars.forEach(car => {
      const portId = car.port?.id || car.port_id;
      if (!portId) return;

      const code = car.port?.code || car.port_code || '';
      const portName = car.port?.port || code || '';
      const country = car.port?.country || '';

      portMap.set(portId, {
        id: portId,
        code,
        port: portName,
        country,
      });
    });

    return Array.from(portMap.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [cars]);

  useEffect(() => {
    if (!selectedPortId && availablePorts.length > 0) {
      setSelectedPortId(availablePorts[0].id);
    } else if (selectedPortId && !availablePorts.find(p => p.id === selectedPortId)) {
      setSelectedPortId('');
    }
  }, [availablePorts, selectedPortId]);

  const filteredCars = useMemo(() => {
    return cars.filter(car => car.loaded && (!selectedPortId || car.port?.id === selectedPortId));
  }, [cars, selectedPortId]);

  const shipperGroups = useMemo<ShipperGroup[]>(() => {
    const groups = new Map<string, ShipperGroup>();

    filteredCars.forEach(car => {
      const key = car.shipper?.id || car.shipper_id || 'unknown';
      const shipperId = car.shipper?.code || 'N/A';
      const shipperName = car.shipper?.name || car.shipper?.shortname || '';
      const makeModel = extractMakeModel(car);
      const weight = Number(car.weight || car.model?.weight || 0);
      const cbm = Number(car.cbm || car.model?.cbm || 0);

      // Calculate freight/collect including additional fees (like invoice)
      let freightValue: string;
      if (car.prepaid_or_collect === 'P') {
        freightValue = 'prepaid';
      } else {
        // Base collect amount
        const baseCollect = (car.collect ?? car.freight) || 0;

        // Add additional fees that apply to collect (type 'C' or 'B')
        let collectFees = 0;
        if (car.additional_fees && typeof car.additional_fees === 'object') {
          Object.values(car.additional_fees).forEach((feeValue) => {
            // Handle both old format (number) and new format ({ amount, type })
            if (typeof feeValue === 'number' && feeValue !== 0) {
              // Old format: default to freight type, so don't add to collect
              // (only add if explicitly type 'C' or 'B')
            } else if (typeof feeValue === 'object' && feeValue !== null && 'amount' in feeValue) {
              const fee = feeValue as AdditionalFeeValue;
              // Add fees with type 'C' (Collect) or 'B' (Both)
              if (fee.amount !== 0 && (fee.type === 'C' || fee.type === 'B')) {
                collectFees += fee.amount;
              }
            }
          });
        }

        const totalCollect = baseCollect + collectFees;
        // European format, comma as decimal separator, no thousands separator.
        // ',-' means "no cents", so it only applies to whole amounts.
        const rounded = Math.round(totalCollect * 100) / 100;
        freightValue = rounded % 1 === 0
          ? `${rounded},-`
          : rounded.toFixed(2).replace('.', ',');
      }

      if (!groups.has(key)) {
        groups.set(key, {
          shipperId,
          shipperName,
          cars: [],
        });
      }

      groups.get(key)!.cars.push({
        blNo: car.bl_number || (car.reference_number ? `CLP${car.reference_number}` : ''),
        consignee: car.consignee_name || car.consignee?.name || '',
        chassisNo: car.chassis_number,
        packages: 1,
        contents: makeModel.trim() || '-',
        weightKg: weight,
        measurementM3: cbm,
        freight: freightValue,
      });

      // Add related cars (sorted: Attached first, then STC)
      if (car.related_cars && car.related_cars.length > 0) {
        const sortedRelatedCars = sortRelatedCarsByType(car.related_cars);

        sortedRelatedCars.forEach(rc => {
          const rcMakeModel = extractMakeModel(rc);
          groups.get(key)!.cars.push({
            blNo: '',
            consignee: '',
            chassisNo: rc.chassis_number,
            packages: 1,
            contents: rcMakeModel || '-',
            weightKg: 0,
            measurementM3: 0,
            freight: '',
            isRelated: true,
            relationshipType: rc.relationship_type || 'STC',
          });
        });
      }
    });

    return Array.from(groups.values()).filter(group => group.cars.length > 0).sort((a, b) => a.shipperId.localeCompare(b.shipperId));
  }, [filteredCars]);

  const selectedPort = useMemo(() => availablePorts.find(p => p.id === selectedPortId), [availablePorts, selectedPortId]);

  const manifestPayload = useMemo(() => {
    if (!selectedVoyage) return null;
    const toPortLabel = selectedPort ? `${selectedPort.code} - ${selectedPort.port}` : 'All Ports';
    const formattedDate = new Date(selectedVoyage.departure).toLocaleDateString('en-GB');

    return {
      vesselName: selectedVoyage.vessel_description,
      date: formattedDate,
      voyageNo: selectedVoyage.voyage_code,
      fromPort: selectedVoyage.port_name || '',
      toPort: toPortLabel,
      shipperGroups,
      includeRelated,
      manifestType,
    };
  }, [selectedVoyage, selectedPort, shipperGroups, includeRelated, manifestType]);

  const manifestFilename = useMemo(() => {
    if (!selectedVoyage) return '';
    const toPortLabel = selectedPort ? `${selectedPort.code} - ${selectedPort.port}` : 'All Ports';
    const typeLabel = manifestType === 'cargo' ? 'Cargo Manifest' : 'Freight Manifest';
    return `${typeLabel} ${selectedVoyage.voyage_code} ${toPortLabel}.pdf`;
  }, [selectedVoyage, selectedPort, manifestType]);

  const generateManifest = async () => {
    if (!manifestPayload || !manifestFilename) return;

    setGenerating(true);
    try {
      await printService.generateFreightManifest(manifestPayload, manifestFilename);
    } catch (error) {
      console.error('Error generating freight manifest:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Manifest</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Select voyage and destination port to generate the manifest grouped by shippers.
          </p>

          <div className="flex gap-4 items-center flex-wrap">
            <div className="w-52">
              <SearchableSelect
                options={sortedVoyages.map(voyage => ({
                  value: voyage.id,
                  label: `${voyage.voyage_code} - ${voyage.vessel_description}`
                }))}
                value={selectedVoyage?.id || ''}
                onChange={(value) => {
                  const voyage = sortedVoyages.find(v => v.id === value);
                  setSelectedVoyage(voyage || null);
                }}
                placeholder="-- Select Voyage --"
              />
            </div>

            <div className="w-52">
              <SearchableSelect
                options={availablePorts.map(port => ({
                  value: port.id,
                  label: `${port.code} - ${port.port}`
                }))}
                value={selectedPortId}
                onChange={setSelectedPortId}
                placeholder="-- Select Port --"
                disabled={availablePorts.length === 0}
              />
            </div>

            <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="manifestType"
                  checked={manifestType === 'freight'}
                  onChange={() => setManifestType('freight')}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Freight</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="manifestType"
                  checked={manifestType === 'cargo'}
                  onChange={() => setManifestType('cargo')}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Cargo</span>
              </label>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeRelated}
                onChange={(e) => setIncludeRelated(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Include Related</span>
            </label>

            <button
              onClick={generateManifest}
              disabled={!selectedVoyage || shipperGroups.length === 0 || generating || loading}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors"
            >
              {generating ? 'Generating...' : 'Save as PDF'}
            </button>
          </div>

          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {!selectedVoyage && 'Choose a voyage to load cars.'}
            {selectedVoyage && loading && 'Loading cars...'}
            {selectedVoyage && !loading && shipperGroups.length === 0 && 'No cars found for this port.'}
            {selectedVoyage && !loading && shipperGroups.length > 0 && `${shipperGroups.length} shipper(s) ready for manifest.`}
          </div>
        </div>

      {loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Loading cars...</p>
        </div>
      )}

      {selectedVoyage && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200 dark:bg-gray-900 dark:border-gray-700">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Preview</span>
            {manifestPayload && (
              <span className="text-sm text-gray-400 ml-2">
                ({manifestPayload.shipperGroups.length} shipper{manifestPayload.shipperGroups.length === 1 ? '' : 's'})
              </span>
            )}
          </div>
          <div className="p-4">
            {manifestPayload && <FreightManifest {...manifestPayload} includeRelated={includeRelated} manifestType={manifestType} />}
          </div>
        </div>
      )}

      {!selectedVoyage && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <div className="text-gray-400 dark:text-gray-500">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>Select a voyage to preview the manifest</p>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default FreightManifestPage;

